import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../lib/axiosInstance';

function StatCard({ label, value, delta, color = 'var(--text)' }) {
    return (
        <div className="flex-1 min-w-[220px] card p-4 shadow">
            <div className="text-xs text-[var(--muted)]">{label}</div>
            <div className="flex items-baseline gap-2 mt-1.5">
                <div className="text-2xl font-bold" style={{ color }}>{value}</div>
                {delta != null && (
                    <div className={delta >= 0 ? 'text-emerald-600 text-xs' : 'text-red-600 text-xs'}>
                        {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
                    </div>
                )}
            </div>
        </div>
    );
}

function Table({ rows }) {
    return (
        <div className="card overflow-hidden">
            <div className="p-4 border-b border-[var(--border)] font-semibold">Recent Orders</div>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[var(--text)]">
                    <thead>
                        <tr>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Order ID</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Customer</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Total</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Status</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-[var(--border)]">
                                <td className="p-3">#{r.id}</td>
                                <td className="p-3">{r.customer}</td>
                                <td className="p-3">₹{r.total.toLocaleString('en-IN')}</td>
                                <td className="p-3">
                                    <span className={r.status === 'Paid' ? 'px-2 py-1 rounded-full text-xs border border-[var(--border)] text-emerald-500 bg-emerald-500/15' : 'px-2 py-1 rounded-full text-xs border border-[var(--border)] text-amber-500 bg-amber-500/15'}>
                                        {r.status}
                                    </span>
                                </td>
                                <td className="p-3">{r.date}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function UsersTable({ users }) {
    return (
        <div className="card overflow-hidden">
            <div className="p-4 border-b border-[var(--border)] font-semibold">Recent Users</div>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[var(--text)]">
                    <thead>
                        <tr>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">User</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Email</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Orders</th>
                            <th className="text-left p-3 text-xs text-[var(--muted)]">Joined</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.email} className="border-t border-[var(--border)]">
                                <td className="p-3">{u.name}</td>
                                <td className="p-3">{u.email}</td>
                                <td className="p-3">{u.orders}</td>
                                <td className="p-3">{u.joined}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function getArcPath(cx, cy, r, startAngle, endAngle) {
    const rad = (deg) => (deg - 90) * (Math.PI / 180);
    const x1 = cx + r * Math.cos(rad(startAngle));
    const y1 = cy + r * Math.sin(rad(startAngle));
    const x2 = cx + r * Math.cos(rad(endAngle));
    const y2 = cy + r * Math.sin(rad(endAngle));
    const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

function OrderPie({ data }) {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    let currentAngle = 0;
    const colors = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

    return (
        <div className="card p-4">
            <div className="font-semibold mb-2">Order Analysis</div>
            <div className="flex items-center gap-4">
                <svg width="160" height="160" viewBox="0 0 160 160">
                    {data.map((d, i) => {
                        const slice = (d.value / total) * 360;
                        const start = currentAngle;
                        const end = currentAngle + slice;
                        currentAngle = end;
                        return (
                            <path key={d.label} d={getArcPath(80, 80, 70, start, end)} fill={colors[i % colors.length]} stroke="#fff" strokeWidth="1" />
                        );
                    })}
                    <circle cx="80" cy="80" r="44" fill="var(--surface)" />
                    <text x="80" y="80" textAnchor="middle" dominantBaseline="middle" fontSize="14" fill="var(--text)" fontWeight="600">{total}</text>
                </svg>
                <div className="grid gap-2">
                    {data.map((d, i) => (
                        <div key={d.label} className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors[i % colors.length] }} />
                            <span className="w-[90px]">{d.label}</span>
                            <span className="text-[var(--muted)]">{d.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function LineChart({ data, title = 'Line', color = '#10b981' }) {
    // data: [{ x: '2025-01-01', y: 123 }]
    const svgRef = useRef(null);
    const [hoverIdx, setHoverIdx] = useState(null);
    if (!data || data.length === 0) return (
        <div className="card p-4">
            <div className="font-semibold mb-2">{title}</div>
            <div className="text-[var(--muted)] text-sm">No data</div>
        </div>
    );

    const width = 500;
    const height = 240;
    const padding = 40;
    const xs = data.map(d => new Date(d.x).getTime());
    const ys = data.map(d => d.y || 0);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = 0;
    const maxY = Math.max(...ys) || 1;
    const sx = (t) => padding + ((t - minX) / (maxX - minX || 1)) * (width - padding * 2);
    const sy = (v) => height - padding - ((v - minY) / (maxY - minY || 1)) * (height - padding * 2);
    const path = data
        .map((d, i) => `${i === 0 ? 'M' : 'L'} ${sx(new Date(d.x).getTime())} ${sy(d.y || 0)}`)
        .join(' ');

    // Build ticks
    const yTicks = 5;
    const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((i * maxY) / yTicks));
    const xTicks = Math.min(5, data.length);
    const xTickIdxs = Array.from({ length: xTicks }, (_, i) => Math.round((i * (data.length - 1)) / (xTicks - 1 || 1)));

    function onMouseMove(e) {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const mouseX = e.clientX - rect.left;
        // invert scale to time
        const t = minX + ((mouseX - padding) / (width - padding * 2)) * (maxX - minX);
        // find nearest index by x
        let nearest = 0;
        let best = Infinity;
        for (let i = 0; i < xs.length; i++) {
            const dx = Math.abs(xs[i] - t);
            if (dx < best) { best = dx; nearest = i; }
        }
        setHoverIdx(nearest);
    }

    function onMouseLeave() {
        setHoverIdx(null);
    }

    return (
        <div className="card p-4">
            <div className="font-semibold mb-2">{title}</div>
            <svg
                ref={svgRef}
                width={width}
                height={height}
                className="max-w-full h-auto"
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
            >
                <rect x="0" y="0" width={width} height={height} fill="var(--surface)" />

                {/* Axes */}
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="var(--border)" />
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="var(--border)" />

                {/* Y ticks */}
                {yTickValues.map((v, i) => (
                    <g key={`y${i}`}>
                        <line x1={padding - 4} y1={sy(v)} x2={padding} y2={sy(v)} stroke="var(--muted)" />
                        <text x={padding - 8} y={sy(v)} dy="0.32em" textAnchor="end" fontSize="10" fill="var(--muted)">{v}</text>
                    </g>
                ))}

                {/* X ticks */}
                {xTickIdxs.map((idx, i) => {
                    const x = sx(xs[idx]);
                    const label = new Date(data[idx].x).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
                    return (
                        <g key={`x${i}`}>
                            <line x1={x} y1={height - padding} x2={x} y2={height - padding + 4} stroke="var(--muted)" />
                            <text x={x} y={height - padding + 16} textAnchor="middle" fontSize="10" fill="var(--muted)">{label}</text>
                        </g>
                    );
                })}

                {/* Line path */}
                <path d={path} fill="none" stroke={color} strokeWidth="2" />

                {/* Points */}
                {data.map((d, i) => (
                    <g key={i}>
                        <circle cx={sx(new Date(d.x).getTime())} cy={sy(d.y || 0)} r={hoverIdx === i ? 3.5 : 2.5} fill={color} />
                    </g>
                ))}

                {/* Hover guideline and tooltip */}
                {hoverIdx != null && (
                    (() => {
                        const hx = sx(xs[hoverIdx]);
                        const hy = sy(ys[hoverIdx]);
                        const dateText = new Date(data[hoverIdx].x).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
                        const valueText = `${ys[hoverIdx]}`;
                        const tipX = Math.min(Math.max(hx + 8, padding + 4), width - padding - 110);
                        const tipY = Math.min(Math.max(hy - 30, padding + 4), height - padding - 40);
                        return (
                            <g>
                                <line x1={hx} y1={padding} x2={hx} y2={height - padding} stroke={color} strokeDasharray="4 4" />
                                <circle cx={hx} cy={hy} r="4" fill={color} />
                                <rect x={tipX} y={tipY} width="110" height="34" rx="4" ry="4" fill="rgba(17,24,39,0.9)" />
                                <text x={tipX + 6} y={tipY + 14} fontSize="11" fill="#fff">{dateText}</text>
                                <text x={tipX + 6} y={tipY + 26} fontSize="12" fill="#fff" fontWeight="600">{valueText}</text>
                            </g>
                        );
                    })()
                )}
            </svg>
        </div>
    );
}

function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [daily, setDaily] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [dailyOrders, setDailyOrders] = useState([]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                setLoading(true);
                setError('');
                const [s, d, t] = await Promise.all([
                    axiosInstance.get(`/brand/orders/analytics/summary?days=${days}`),
                    axiosInstance.get(`/brand/orders/analytics/daily?days=${days}`),
                    axiosInstance.get(`/brand/orders/analytics/top-products?days=${days}&limit=5`)
                ]);
                if (cancelled) return;
                setSummary(s.data?.data || null);
                const dRows = (d.data?.data || []).map(r => ({ x: r.day, y: Number(r.revenue || 0), orders: Number(r.orders || 0) }));
                setDaily(dRows.map(r => ({ x: r.x, y: r.y })));
                setDailyOrders(dRows.map(r => ({ x: r.x, y: r.orders })));
                setTopProducts(t.data?.data || []);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.message || 'Failed to load analytics');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true };
    }, [days]);

    const stats = useMemo(() => {
        const rev = summary?.window?.windowRevenue || 0;
        const ord = summary?.window?.windowOrders || 0;
        const items = summary?.window?.windowItems || 0;
        const aov = ord > 0 ? Math.round(rev / ord) : 0;
        const totalOrders = summary?.orders?.totalOrders || 0;
        const pendingOrders = summary?.orders?.pendingOrders || 0;
        const deliveredOrders = summary?.orders?.deliveredOrders || 0;
        const shippedOrders = summary?.orders?.shippedOrders || 0;
        const codAmount = summary?.payments?.codAmount || 0;
        const prepaidAmount = summary?.payments?.prepaidAmount || 0;
        const productTotal = summary?.products?.total || 0;
        return [
            { label: 'TOTAL ORDERS', value: totalOrders },
            { label: 'PENDING ORDERS', value: pendingOrders },
            { label: 'SHIPPED ORDERS', value: shippedOrders },
            { label: 'DELIVERED ORDERS', value: deliveredOrders },
            { label: 'TOTAL PRODUCTS', value: productTotal },
            { label: 'COD ORDER AMOUNT TOTAL', value: `₹${Number(codAmount).toLocaleString('en-IN')}` },
            { label: 'PREPAID ORDER AMOUNT', value: `₹${Number(prepaidAmount).toLocaleString('en-IN')}` },
            { label: `Revenue (${days}d)`, value: `₹${Number(rev).toLocaleString('en-IN')}` },
            { label: `Orders (${days}d)`, value: ord },
            { label: 'Avg. Order Value', value: `₹${aov.toLocaleString('en-IN')}` },
            { label: `Products (${days}d)`, value: items }
        ];
    }, [summary, days]);

    const orderPie = useMemo(() => {
        const sc = summary?.statusCounts || {};
        return [
            { label: 'Pending', value: sc.pending || 0 },
            { label: 'Shipped', value: sc.shipped || 0 },
            { label: 'Delivered', value: sc.delivered || 0 }
        ];
    }, [summary]);

    const topRows = useMemo(() => {
        return (topProducts || []).map((p, idx) => ({
            id: `${p.name}__${p.variationName || ''}__${idx}`,
            name: p.name,
            variationName: p.variationName,
            itemCount: p.itemCount,
            revenue: p.revenue
        }));
    }, [topProducts]);

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-[1200px] mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="lux-heading m-0 text-2xl">Dashboard</h1>
                    <div className="flex items-center gap-2">
                        <select
                            value={days}
                            onChange={(e) => setDays(Number(e.target.value))}
                            className="px-2 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text)] text-sm"
                        >
                            <option value={7}>Last 7 days</option>
                            <option value={14}>Last 14 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {stats.map((s) => (
                        <StatCard key={s.label} label={s.label} value={s.value} />
                    ))}
                </div>

                <div className="h-4" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <OrderPie data={orderPie} />
                    <LineChart data={daily} title="Revenue (Daily)" color="#10b981" />
                </div>

                <div className="h-4" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <LineChart data={dailyOrders} title="Orders (Daily)" color="#3b82f6" />
                </div>

                <div className="h-4" />

                <div className="card overflow-hidden">
                    <div className="p-4 border-b border-[var(--border)] font-semibold">Top Products (by Revenue)</div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-[var(--text)]">
                            <thead>
                                <tr>
                                    <th className="text-left p-3 text-xs text-[var(--muted)]">Product</th>
                                    <th className="text-left p-3 text-xs text-[var(--muted)]">Variation</th>
                                    <th className="text-left p-3 text-xs text-[var(--muted)]">Items</th>
                                    <th className="text-left p-3 text-xs text-[var(--muted)]">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topRows.map(r => (
                                    <tr key={r.id} className="border-t border-[var(--border)]">
                                        <td className="p-3">{r.name}</td>
                                        <td className="p-3">{r.variationName}</td>
                                        <td className="p-3">{r.itemCount}</td>
                                        <td className="p-3">₹{Number(r.revenue || 0).toLocaleString('en-IN')}</td>
                                    </tr>
                                ))}
                                {!loading && topRows.length === 0 && (
                                    <tr>
                                        <td className="p-3 text-[var(--muted)]" colSpan={4}>No products found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;


